import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useUserCapabilities } from "@/hooks/useUserCapabilities";
import { TestCleanLauncher } from "@/components/owner/TestCleanLauncher";
import { PendingUploads } from "@/components/cleaner/PendingUploads";
import { OfflineLine } from "@/components/cleaner/OfflineLine";
import { getWaitingForTask, retryFailed, useOutboxCounts, useOutboxVersion } from "@/lib/offline/outbox";
import { useRefreshVisibleTurnoverWindows } from "@/hooks/useRefreshTurnoverWindow";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ChevronRight, Wrench } from "lucide-react";
import Navbar from "@/components/Navbar";
import { useCleanerRecord } from "@/hooks/useCleaner";
import { useInProgressCleaningTasks } from "@/hooks/useInProgressCleaningTasks";
import { useMyTasks, useMyPreventativeTasks, type MyTaskItem } from "@/hooks/useMyTasks";
import { Badge } from "@/components/ui/badge";
import { format, isToday, isTomorrow } from "date-fns";
import { formatScheduledDate, parseLocalDateOnly } from "@/lib/dateUtils";
import { canStartTurnoverClean, formatTurnoverOpensHint, type TurnoverChipState } from "@/lib/turnoverWindow";
import { cn } from "@/lib/utils";
import {
  clearCleanerResumeCleanUrl,
  clearCleanerTurnoverSessionForTask,
  getCleanerResumeCleanUrl,
  parseCleanerResumeCleanUrl,
} from "@/lib/cleanerVisitPersistence";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageContainer } from "@/components/layout/PageContainer";
import { useTranslation } from "@/hooks/useTranslation";
import { PastCleaningsTab } from "@/components/cleaner/PastCleaningsTab";
import { InstallAppCard } from "@/components/InstallAppCard";
import CleanerEmailVerificationBanner from "@/components/CleanerEmailVerificationBanner";
import { ProfileCompletionModal } from "@/components/cleaner/ProfileCompletionModal";
import { useToast } from "@/hooks/use-toast";
import { WorkspaceChip } from "@/components/layout/WorkspaceChip";
import { showJobsWorkspaceChip } from "@/lib/workspaceMode";
import { useHostLabels } from "@/hooks/useHostLabels";
import { AppTour } from "@/components/tour/AppTour";
import { useAppTour } from "@/hooks/useAppTour";

const CleanerDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, tDynamic } = useTranslation();
  const { toast } = useToast();
  const { user, profile, loading: authLoading } = useAuth();
  useOutboxVersion(); // job cards show "Waiting to send" while their photos are still on this phone
  const outboxCounts = useOutboxCounts();
  const [pendingOpenId, setPendingOpenId] = useState<string | null>(null);
  const { is_cleaner, is_owner, is_cohost, has_cleaner_assignment } = useUserCapabilities();
  const { data: cleaner, isLoading: cleanerLoading } = useCleanerRecord();
  const { data: inProgressTasks = [], isLoading: inProgressLoading } = useInProgressCleaningTasks();
  const { data: allMyTasks = [], isLoading: myTasksLoading } = useMyTasks("week");
  useRefreshVisibleTurnoverWindows(allMyTasks);
  const { data: pmTasks = [], isLoading: pmTasksLoading } = useMyPreventativeTasks();
  const { activeTour, closeTour } = useAppTour("cleaner-dashboard");

  const isLoading = authLoading || cleanerLoading || inProgressLoading || myTasksLoading || pmTasksLoading;

  const filterPropertyId = searchParams.get("property_id") ?? null;
  const tasksForView = filterPropertyId
    ? allMyTasks.filter((t) => t.property_id === filterPropertyId)
    : allMyTasks;
  const showJobsChip = showJobsWorkspaceChip({ is_cleaner, is_owner, is_cohost, has_cleaner_assignment });
  const jobPropertyIds = useMemo(
    () => [...new Set(allMyTasks.map((t) => t.property_id).filter(Boolean))],
    [allMyTasks],
  );
  const { data: hostLabels = {} } = useHostLabels(showJobsChip ? jobPropertyIds : []);
  const inProgressIds = useMemo(() => new Set(inProgressTasks.map((t) => t.id)), [inProgressTasks]);

  const todayTask = tasksForView.find((t) => isToday(parseLocalDateOnly(t.scheduled_date)));
  const futureTasks = tasksForView.filter((t) => !isToday(parseLocalDateOnly(t.scheduled_date)));

  /** Tasks open under "In progress" are not duplicated on today's card or the upcoming list. */
  const todayTaskVisible = todayTask && !inProgressIds.has(todayTask.id) ? todayTask : null;
  const futureTasksVisible = futureTasks.filter((t) => !inProgressIds.has(t.id));

  const [scheduleTab, setScheduleTab] = useState<"in-progress" | "upcoming" | "past">("upcoming");
  const didInitScheduleTab = useRef(false);
  const [resumeCleanUrl, setResumeCleanUrl] = useState<string | null>(null);
  const [pastEvidenceOpen, setPastEvidenceOpen] = useState(false);

  const syncResumeCleanUrl = useCallback(() => {
    const stored = getCleanerResumeCleanUrl();
    if (!stored) {
      setResumeCleanUrl(null);
      return;
    }
    const parsed = parseCleanerResumeCleanUrl(stored);
    if (!parsed) {
      clearCleanerResumeCleanUrl();
      setResumeCleanUrl(null);
      return;
    }
    if (!inProgressIds.has(parsed.taskId)) {
      clearCleanerTurnoverSessionForTask(parsed.taskId);
      setResumeCleanUrl(null);
      return;
    }
    setResumeCleanUrl(stored);
  }, [inProgressIds]);

  useEffect(() => {
    if (inProgressLoading) return;
    syncResumeCleanUrl();
  }, [inProgressLoading, syncResumeCleanUrl]);

  useEffect(() => {
    const onFocus = () => {
      if (!inProgressLoading) syncResumeCleanUrl();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [inProgressLoading, syncResumeCleanUrl]);
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam === "upcoming" || tabParam === "in-progress" || tabParam === "past") {
      setScheduleTab(tabParam);
      didInitScheduleTab.current = true;
    }
  }, [searchParams]);
  useEffect(() => {
    if (scheduleTab !== "past") setPastEvidenceOpen(false);
  }, [scheduleTab]);
  useEffect(() => {
    if (inProgressLoading) return;
    if (inProgressTasks.length > 0 && !didInitScheduleTab.current) {
      setScheduleTab("in-progress");
      didInitScheduleTab.current = true;
    }
  }, [inProgressLoading, inProgressTasks.length]);

  // No auto-redirect to task from schedule: let the user stay on "My tasks" and open the task when they choose (so "Back to my tasks" works).
  if (isLoading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center bg-background">
          <p>{t("loading")}</p>
        </div>
      </AppLayout>
    );
  }

  if (!user) {
    return (
      <AppLayout>
        <Navbar />
        <div className="flex items-center justify-center p-4" style={{ minHeight: "calc(100vh - 57px)" }}>
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>{t("accessDenied")}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                {t("signInToContinue") || "Sign in to view your cleaning tasks."}
              </p>
              <Button onClick={() => navigate("/login")} className="w-full">
                {t("goToLogin")}
              </Button>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  const formatDateLabel = (dateStr: string) => {
    const date = parseLocalDateOnly(dateStr);
    if (isToday(date)) return t("todaysTask");
    if (isTomorrow(date)) return format(date, "EEE, MMM d");
    return format(date, "EEE, MMM d");
  };

  const openTask = (task: Pick<MyTaskItem, "id" | "property_id" | "scheduled_date" | "status" | "assigned_type" | "can_start" | "window_hint">) => {
    const isCleanerRunner = Boolean(cleaner?.id || profile?.role === "cleaner");
    const allowed = task.can_start ?? (task.status === "in_progress" || canStartTurnoverClean(task.scheduled_date));
    if (isCleanerRunner && task.status === "scheduled" && !allowed) {
      toast({
        title: t("notOpenYet"),
        description: task.window_hint || formatTurnoverOpensHint(task.scheduled_date) || t("turnoverWindowNotStarted"),
        variant: "default",
      });
      return;
    }
    // On worker schedule, users with cleaner identity should always resume via cleaner portal.
    // This preserves in-progress continuation and avoids owner/team runner permission checks.
    if (cleaner?.id || profile?.role === "cleaner") {
      navigate(`/clean/${task.property_id}?taskId=${task.id}`);
      return;
    }
    if (task.assigned_type === "cleaner") {
      navigate(`/clean/${task.property_id}?taskId=${task.id}`);
      return;
    }
    navigate(`/tasks/run/${task.property_id}?taskId=${task.id}`);
  };

  const openPreventativeTask = (task: { id: string; property_id: string }) => {
    navigate(`/clean/${task.property_id}?taskId=${task.id}`);
  };

  const maintenanceDueSection =
    pmTasks.length > 0 ? (
      <div className={futureTasksVisible.length > 0 ? "mt-6" : ""}>
        <div className="flex items-center gap-2 mb-3">
          <Wrench className="h-4 w-4 text-amber-500" />
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            {t("maintenanceDue")}
          </h3>
        </div>
        <div className="space-y-2">
          {pmTasks.map((task) => (
            <Card
              key={task.id}
              className="cursor-pointer hover:bg-muted/40 transition-colors"
              onClick={() => openPreventativeTask(task)}
            >
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {task.notes?.replace("Preventative maintenance: ", "") ?? t("maintenance")}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {(task.properties as { name?: string })?.name ?? t("property")} · {formatDateLabel(task.scheduled_date)}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {isToday(parseLocalDateOnly(task.scheduled_date)) ? (
                    <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">{t("dueToday")}</Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">{formatScheduledDate(task.scheduled_date, "MMM d")}</Badge>
                  )}
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    ) : null;

  return (
    <AppLayout>
      <ProfileCompletionModal />
      <Navbar />
      
      <PageContainer className="py-4 pb-24">
        <CleanerEmailVerificationBanner />

        {resumeCleanUrl && (cleaner?.id || profile?.role === "cleaner") ? (
          <Alert className="mb-4 border-primary/30 bg-primary/5">
            <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-sm text-foreground">{tDynamic("You have a turnover in progress. Resume to keep your timer and saved photos.")}</span>
              <Button size="sm" className="shrink-0 w-full sm:w-auto" onClick={() => navigate(resumeCleanUrl)}>
                {tDynamic("Resume turnover")}
              </Button>
            </AlertDescription>
          </Alert>
        ) : null}
        
        {!pastEvidenceOpen ? (
          <div className="mb-3.5 flex flex-col gap-3.5 px-1">
            <div className="flex items-center justify-between gap-3">
              <h1 className="ch2-title-27">{t("myJobs")}</h1>
              {is_owner ? <TestCleanLauncher variant="jobs" /> : null}
            </div>
            {showJobsChip ? <WorkspaceChip active="jobs" /> : null}
            <div className="flex gap-1.5 rounded-[13px] border-[1.5px] border-[#e6edf1] bg-white p-1">
              <button
                type="button"
                onClick={() => {
                  setScheduleTab("upcoming");
                  setPastEvidenceOpen(false);
                }}
                className={cn(
                  "flex h-[38px] flex-1 items-center justify-center rounded-[10px] text-[14px] font-bold",
                  scheduleTab !== "past"
                    ? "bg-[#e2eeed] text-[#15776d]"
                    : "bg-transparent text-[#64748b]",
                )}
              >
                {t("jobs")}
              </button>
              <button
                type="button"
                data-tour="cleaner-past-tab"
                onClick={() => setScheduleTab("past")}
                className={cn(
                  "flex h-[38px] flex-1 items-center justify-center rounded-[10px] text-[14px] font-bold",
                  scheduleTab === "past"
                    ? "bg-[#e2eeed] text-[#15776d]"
                    : "bg-transparent text-[#64748b]",
                )}
              >
                {t("past")}
              </button>
            </div>
          </div>
        ) : null}

        {scheduleTab !== "past" && !pastEvidenceOpen ? (
          <InstallAppCard className="mb-4" userId={user?.id} />
        ) : null}

        {scheduleTab !== "past" ? (
          (() => {
            const clockById = new Map(allMyTasks.map((t) => [t.id, t]));
            const jobRows: MyTaskItem[] = [
              ...inProgressTasks.map((task) => {
                const clock = clockById.get(task.id);
                return {
                  id: task.id,
                  property_id: task.property_id,
                  scheduled_date: task.scheduled_date,
                  status: task.status,
                  property_name: task.property_name || clock?.property_name || null,
                  checklist_name: clock?.checklist_name ?? null,
                  assigned_type: task.assigned_type || clock?.assigned_type || "cleaner",
                  reservation_id: clock?.reservation_id,
                  window_label: clock?.window_label,
                  window_hint: clock?.window_hint,
                  window_chip: clock?.window_chip,
                  window_times: clock?.window_times,
                  window_times_secondary: clock?.window_times_secondary,
                  window_times_tone: clock?.window_times_tone,
                  guest_left: clock?.guest_left,
                  waiting_on_guest: clock?.waiting_on_guest,
                  same_day: clock?.same_day,
                  can_start: clock?.can_start ?? true,
                  is_test: clock?.is_test,
                  property_photo_url: clock?.property_photo_url,
                  property_address_line: clock?.property_address_line,
                  property_address_sub: clock?.property_address_sub,
                  property_street_number: clock?.property_street_number,
                } satisfies MyTaskItem;
              }),
              ...(todayTaskVisible ? [todayTaskVisible] : []),
              ...futureTasksVisible,
            ];
            const seen = new Set<string>();
            const unique = jobRows.filter((task) => {
              if (seen.has(task.id)) return false;
              seen.add(task.id);
              return true;
            });

            if (unique.length === 0) {
              return (
                <div
                  className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-8 pb-8 text-center"
                  data-tour="cleaner-jobs-empty"
                >
                  <div className="flex h-[78px] w-[78px] items-center justify-center rounded-[22px] bg-[#e2eeed]">
                    <svg
                      width="34"
                      height="34"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#24a89a"
                      strokeWidth="2.1"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 12.5l5 5L20 6.5" />
                    </svg>
                  </div>
                  <div className="flex flex-col gap-[7px]">
                    <div className="text-[27px] font-bold tracking-[-0.6px] text-[#0f1720]">
                      {t("noJobsYet")}
                    </div>
                    <div className="text-[15px] font-medium leading-[1.45] text-[#64748b]">
                      {t("noJobsYetHint")}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setScheduleTab("past")}
                    className="mt-1 flex min-h-12 items-center rounded-[13px] border-[1.5px] border-[#e6edf1] bg-white px-5 text-[16px] font-bold text-[#15776d]"
                  >
                    {t("viewPast")}
                  </button>
                </div>
              );
            }

            return (
              <div className="flex flex-col gap-3" data-tour="cleaner-upcoming-tasks">
                <OfflineLine compact />
                {outboxCounts.failed > 0 ? (
                  <div className="flex flex-col gap-3 rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white p-4">
                    <div className="text-[17px] font-bold text-[#0f1720]">{t("failTitle")}</div>
                    <div className="text-[14.5px] font-medium leading-snug text-[#475569]">{t("failBody")}</div>
                    <button
                      type="button"
                      onClick={() => void retryFailed()}
                      className="flex h-12 items-center justify-center rounded-[14px] bg-[#24a89a] text-[16px] font-bold text-white"
                    >
                      {t("failBtn")}
                    </button>
                  </div>
                ) : null}
                {unique.map((task) => {
                  const inProg = inProgressIds.has(task.id) || task.status === "in_progress";
                  const chip = (inProg ? "In progress" : task.window_chip || "Open") as TurnoverChipState;
                  const windowLabel = task.window_label || `Turnover · ${task.scheduled_date}`;
                  const upcoming = chip === "Upcoming" && !inProg;
                  const clock = task.window_times || null;
                  const clockSecondary =
                    task.window_times_secondary ||
                    (upcoming
                      ? task.window_hint || formatTurnoverOpensHint(task.scheduled_date) || t("notOpenYet")
                      : null);
                  const canStart = inProg || (task.can_start ?? canStartTurnoverClean(task.scheduled_date));
                  const overdue = chip === "Overdue";
                  const guestLeft = Boolean(task.guest_left);
                  const waitingOnGuest = Boolean(task.waiting_on_guest);
                  const tone = task.window_times_tone;
                  const btnLabel = inProg ? t("continue") : t("start");
                  const clockColor =
                    upcoming || tone === "muted"
                      ? "text-[#64748b]"
                      : waitingOnGuest || tone === "amber"
                        ? "text-[#9a6700]"
                        : guestLeft || tone === "teal"
                          ? "text-[#15776d]"
                          : overdue
                            ? "text-[#b4453a]"
                            : "text-[#0f1720]";
                  const addrLine =
                    task.property_address_line ||
                    (showJobsChip && hostLabels[task.property_id]
                      ? `${task.property_name || t("property")} · ${hostLabels[task.property_id]}`
                      : task.property_name || t("property"));
                  const addrSub =
                    task.property_address_sub ||
                    (task.property_address_line ? windowLabel : null);
                  const photoUrl = task.property_photo_url?.trim() || null;
                  const streetNum = task.property_street_number?.trim() || null;
                  return (
                    <div
                      key={task.id}
                      className="flex items-end gap-3 rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white p-3.5"
                    >
                      <div
                        className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border-[1.5px] border-[rgba(15,23,32,0.07)] bg-[repeating-linear-gradient(135deg,#eef3f6_0_7px,#e2eaef_7px_14px)]"
                        aria-hidden
                      >
                        {photoUrl ? (
                          <img
                            src={photoUrl}
                            alt=""
                            className="h-full w-full object-cover"
                            loading="lazy"
                            referrerPolicy="no-referrer"
                          />
                        ) : streetNum ? (
                          <span className="text-xl font-bold tracking-[-0.3px] text-[#8fa3b0]">
                            {streetNum}
                          </span>
                        ) : null}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-base font-bold tracking-[-0.2px] text-[#0f1720]">
                          {tDynamic(addrLine)}
                        </div>
                        {addrSub ? (
                          <div className="mt-0.5 truncate text-[13px] font-medium text-[#64748b]">
                            {tDynamic(addrSub)}
                          </div>
                        ) : null}
                        {clock ? (
                          <div className={cn("mt-1 text-[17px] font-bold tracking-[-0.2px]", clockColor)}>
                            {tDynamic(clock)}
                          </div>
                        ) : null}
                        {clockSecondary ? (
                          <div className="mt-0.5 text-[13px] font-medium text-[#64748b]">
                            {tDynamic(clockSecondary)}
                          </div>
                        ) : null}
                        {(() => {
                          const w = getWaitingForTask(task.id);
                          const n = w.photos + w.writes;
                          if (n === 0) return null;
                          return (
                            <>
                            <div className="mt-1.5 flex items-center gap-2 text-[12.5px] font-bold text-[#64748b]">
                              <button
                                type="button"
                                onClick={() => setPendingOpenId((cur) => (cur === task.id ? null : task.id))}
                                className="rounded-full bg-[#eef2f5] px-2.5 py-1"
                              >
                                {t("jobTagWaiting")}
                              </button>
                              {w.photos > 0 ? (
                                <span className="font-medium">
                                  {w.photos === 1 ? t("waitingPhotosOne") : t("waitingPhotosMany").replace("{{n}}", String(w.photos))}
                                </span>
                              ) : null}
                            </div>
                            {pendingOpenId === task.id ? <PendingUploads taskId={task.id} className="mt-2" /> : null}
                            </>
                          );
                        })()}
                      </div>
                      <button
                        type="button"
                        disabled={!canStart && !inProg}
                        onClick={() => openTask(task)}
                        className={
                          !canStart && !inProg
                            ? "flex h-[52px] min-w-[104px] shrink-0 items-center justify-center rounded-[14px] border-[1.5px] border-[#e6edf1] bg-[#eef3f6] px-[22px] text-[17px] font-bold text-[#a3b1bd]"
                            : inProg
                              ? "flex h-[52px] min-w-[104px] shrink-0 items-center justify-center rounded-[14px] border-[1.5px] border-[#24a89a] bg-white px-[22px] text-[17px] font-bold text-[#15776d]"
                              : overdue
                                ? "flex h-[52px] min-w-[104px] shrink-0 items-center justify-center rounded-[14px] bg-[#b4453a] px-[22px] text-[17px] font-bold text-white"
                                : "flex h-[52px] min-w-[104px] shrink-0 items-center justify-center rounded-[14px] bg-[#24a89a] px-[22px] text-[17px] font-bold text-white hover:bg-[#1f958a]"
                        }
                      >
                        {btnLabel}
                      </button>
                    </div>
                  );
                })}
              </div>
            );
          })()
        ) : (
          <PastCleaningsTab onEvidenceOpenChange={setPastEvidenceOpen} />
        )}

        {scheduleTab !== "past" ? maintenanceDueSection : null}
      </PageContainer>
      <AppTour tour={activeTour} onClose={closeTour} />
    </AppLayout>
  );
};

export default CleanerDashboard;